/**
 * The one place the app hands text to the share sheet.
 *
 * react-native-web's `Share.share` is a thin wrapper over the Web Share API
 * and REJECTS with "Share is not supported in this browser" wherever
 * `navigator.share` does not exist — which is every desktop Firefox, ever.
 * Found live on stg 2026-09-04: the invite screen minted an envelope (a
 * daily-cap slot, spent), then the share rejected and the link was thrown
 * away unseen, behind an error blaming the mint.
 *
 * On web without the API this copies to the clipboard instead and says so —
 * the person came for a link to give someone, and the clipboard IS that on a
 * desktop. Callers therefore do their work (minting included) before calling
 * this, and trust that the text reaches the user one way or the other.
 *
 * Every caller goes through here; `tests/mobile/share-fallback.test.ts`
 * scans the app source so a direct `Share.share` cannot come back.
 */
import { Platform, Share } from "react-native";

import i18n from "@/i18n";
import { notify } from "@/lib/dialog";

type ShareTextContent = {
  message: string;
  /** iOS-style separate URL; appended to the copied text when not already in the message. */
  url?: string;
  title?: string;
};

export type ShareTextOutcome = "shared" | "copied" | "dismissed";

export async function shareText(
  content: ShareTextContent,
): Promise<ShareTextOutcome> {
  // Platform first: React Native defines a global `navigator` (without
  // `share`) on the phones too, and the clipboard fallback must never run
  // there — the native share sheet always exists.
  const webWithoutShareSheet =
    Platform.OS === "web" &&
    (typeof navigator === "undefined" ||
      typeof navigator.share !== "function");

  if (webWithoutShareSheet) {
    const text =
      content.url && !content.message.includes(content.url)
        ? `${content.message} ${content.url}`
        : content.message;
    // Requires a secure context, which every deployed host is. If it is
    // genuinely absent the throw carries a real reason to the caller's
    // error handling instead of a silent nothing.
    await navigator.clipboard.writeText(text);
    notify(i18n.t("common.linkCopied"));
    return "copied";
  }

  try {
    await Share.share(content);
    return "shared";
  } catch (err) {
    // Closing the web share sheet rejects with AbortError (native resolves
    // with dismissedAction instead). A cancellation is not a failure and
    // must not reach a caller's error dialog.
    if (err instanceof Error && err.name === "AbortError") return "dismissed";
    throw err;
  }
}
